import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../../layouts/AuthLayout';
import { AppCard } from '../../components/ui/AppCard';
import { SectionTitle } from '../../components/ui/SectionTitle';
import { TextField } from '../../components/ui/TextField';
import { Button } from '../../components/ui/Button';
import { Toast } from '../../components/ui/Toast';
import { useStore } from '../../store/StoreContext';

const EMAIL_RE = /\S+@\S+\.\S+/;

// One screen for the whole code-based reset flow (roadmap item #2, replaced
// the emailed link + separate ResetPasswordConfirmScreen): request a code,
// then enter it with the new password.
export function ResetPasswordScreen() {
  const { sendPasswordReset, resetPasswordWithCode } = useStore();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [done, setDone] = useState(false);

  const emailValid = EMAIL_RE.test(email.trim());

  const requestCode = async () => {
    if (!emailValid) return setError('Enter a valid email address first.');
    setSendingCode(true);
    setError('');
    try {
      const result = await sendPasswordReset(email.trim());
      setChallengeId(result.challengeId);
      setNotice(`If an account exists for ${email.trim()}, we sent a 6-digit code. Enter it below with your new password.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSendingCode(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!challengeId) return setError('Tap "Send code" first.');
    if (!code.trim()) return setError('Enter the code from your email.');
    if (password.length < 8) return setError('Password must be at least 8 characters long.');
    if (password !== confirm) return setError('Passwords do not match.');
    setLoading(true);
    try {
      await resetPasswordWithCode(challengeId, code.trim(), password, confirm);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to reset your password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <SectionTitle title="Reset Password" />
      <AppCard>
        {done ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-black/60 dark:text-white/60 m-0">
              Your password has been updated. You can now log in.
            </p>
            <Button type="button" full onClick={() => navigate('/login')}>
              Go to login
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-end gap-2">
                <TextField
                  label="E-mail address"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (challengeId) {
                      setChallengeId('');
                      setCode('');
                      setNotice('');
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="bordered"
                  loading={sendingCode}
                  disabled={sendingCode || !emailValid}
                  onClick={requestCode}
                  className="shrink-0 whitespace-nowrap"
                >
                  {challengeId ? 'Resend' : 'Send code'}
                </Button>
              </div>
              {notice && <span className="text-xs text-black/55 dark:text-white/55">{notice}</span>}
            </div>

            <TextField
              label="Reset code"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder={challengeId ? '6-digit code' : 'Tap "Send code" first'}
              disabled={!challengeId}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <TextField
              label="New password"
              type="password"
              autoComplete="new-password"
              disabled={!challengeId}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <TextField
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              disabled={!challengeId}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            <Button type="submit" full loading={loading} disabled={loading || !challengeId}>
              Reset password
            </Button>
          </form>
        )}
      </AppCard>
      <Link to="/login" className="text-center text-blue-600 dark:text-blue-400 hover:underline text-sm">
        Login
      </Link>
      {error && <Toast message={error} onDismiss={() => setError('')} />}
    </AuthLayout>
  );
}
